import { ReloadOutlined } from "@ant-design/icons";
import { Button, Empty, Table, Tag, Tooltip, Typography, message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { useApiContext } from "../../../utils";

import { createSmartAlertsDebugApi } from "./api";
import { RunDetailPanel } from "./RunDetailPanel";
import { AlertItem, RunItem } from "./types";

const { Text } = Typography;

const PanelWrapper = styled.div`
  background: #fafafa;
  border-radius: 6px;
  padding: 12px 16px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const PanelTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #444;
`;

interface RunHistoryPanelProps {
  alert: AlertItem;
}

const STATUS_COLOR: Record<string, string> = {
  COMPLETED: "green",
  FAILED: "red",
  SKIPPED: "default",
  TRIGGER_NOT_MET: "orange",
  RUNNING: "processing",
};

const TRIGGER_RESULT_COLOR: Record<string, string> = {
  fired: "green",
  not_fired: "default",
  baseline_captured: "blue",
  error: "red",
  preempted: "orange",
  execution_locked: "orange",
};

const TRIGGER_RESULT_LABEL: Record<string, string> = {
  fired: "Fired",
  not_fired: "Not fired",
  baseline_captured: "Baseline",
  error: "Error",
  preempted: "Preempted",
  execution_locked: "Locked",
};

function formatDateTime(iso?: string): string {
  if (!iso) {
    return "-";
  }
  return new Date(iso).toLocaleString();
}

function timeAgo(iso?: string): string {
  if (!iso) {
    return "-";
  }
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return `${hrs}h ago`;
  }
  return `${Math.floor(hrs / 24)}d ago`;
}

function computeDuration(startedAt?: string, completedAt?: string): string {
  if (!startedAt || !completedAt) {
    return "—";
  }
  const diff = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (diff < 0) {
    return "—";
  }
  if (diff < 1000) {
    return `${diff}ms`;
  }
  return `${(diff / 1000).toFixed(1)}s`;
}

function formatDeliveries(run: RunItem): React.ReactNode {
  const parts: string[] = [];
  if (run.emails_sent) {
    parts.push(`${run.emails_sent} email`);
  }
  if (run.slack_messages_sent) {
    parts.push(`${run.slack_messages_sent} Slack`);
  }
  if (run.ms_teams_messages_sent) {
    parts.push(`${run.ms_teams_messages_sent} Teams`);
  }
  if (parts.length === 0) {
    return <Text type="secondary">—</Text>;
  }
  return <Text style={{ fontSize: 12 }}>{parts.join(", ")}</Text>;
}

export const RunHistoryPanel: React.FC<RunHistoryPanelProps> = ({ alert }) => {
  const apiContext = useApiContext();
  const [runs, setRuns] = useState<RunItem[]>([]);
  const [loading, setLoading] = useState(false);
  const alertId = alert.smart_alert_id;

  const loadRuns = useCallback(async () => {
    if (!apiContext?.DEPRECATED_api) {
      return;
    }
    const api = createSmartAlertsDebugApi(apiContext.DEPRECATED_api);
    setLoading(true);
    try {
      const response = await api.getRuns(alertId, { limit: 50 });
      setRuns(response.runs || []);
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to load runs");
    } finally {
      setLoading(false);
    }
  }, [alertId, apiContext]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const columns = [
    {
      title: "Run ID",
      dataIndex: "smart_alert_run_id",
      key: "smart_alert_run_id",
      width: 110,
      render: (id: string) => (
        <Text
          copyable={{ text: id }}
          style={{ fontFamily: "monospace", fontSize: 11, color: "#888" }}
        >
          {id.slice(0, 8)}…
        </Text>
      ),
    },
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      width: 100,
      render: (val: string | undefined) => (
        <Tooltip title={formatDateTime(val)}>
          <Text style={{ fontSize: 12 }}>{timeAgo(val)}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Duration",
      key: "duration",
      width: 80,
      render: (_: unknown, record: RunItem) => (
        <Text style={{ fontSize: 12 }}>
          {computeDuration(record.started_at, record.completed_at)}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: string | undefined) => {
        if (!status) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Tag color={STATUS_COLOR[status] || "default"} style={{ fontSize: 11 }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Trigger",
      dataIndex: "trigger_result",
      key: "trigger_result",
      width: 110,
      render: (val: string | undefined) => {
        if (!val) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Tag color={TRIGGER_RESULT_COLOR[val] || "default"} style={{ fontSize: 11 }}>
            {TRIGGER_RESULT_LABEL[val] || val}
          </Tag>
        );
      },
    },
    {
      title: "Deliveries",
      key: "deliveries",
      width: 150,
      render: (_: unknown, record: RunItem) => formatDeliveries(record),
    },
    {
      title: "Trigger type",
      dataIndex: "trigger_type",
      key: "trigger_type",
      width: 100,
      render: (val: string | undefined) => (
        <Text style={{ fontSize: 11, color: "#aaa" }}>{val || "—"}</Text>
      ),
    },
  ];

  return (
    <PanelWrapper>
      <PanelHeader>
        <PanelTitle>Run History ({runs.length})</PanelTitle>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={loadRuns}
          loading={loading}
          type="text"
        >
          Refresh
        </Button>
      </PanelHeader>

      <Table
        dataSource={runs}
        columns={columns}
        rowKey="smart_alert_run_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
        locale={{
          emptyText: <Empty description="No runs yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
        expandable={{
          expandedRowRender: (record: RunItem) => (
            <div style={{ padding: "8px 0 8px 16px" }}>
              <RunDetailPanel run={record} alert={alert} />
            </div>
          ),
          rowExpandable: () => true,
        }}
      />
    </PanelWrapper>
  );
};
